// Keep canvas text in sync with CoreUI: CSS cannot style ECharts canvas content.
const legacyColors = ['#dddddd', '#738496', '#334b5c', '#60a0a8', '#cccccc', '#7e91a5'];
const darkColors = ['#b7c4d4', '#79b8ed', '#42c7b5', '#65c5cf', '#a6b5c7', '#93b3d5'];
const mapComponents = (value, transform) => Array.isArray(value) ? value.map(transform) : transform(value);

export function themeChartOption(option, { dark, text, background }) {
  const result = { ...option, textStyle: { ...option.textStyle, color: text } };
  const textStyle = style => ({ ...style, color: text });
  const label = style => ({ ...style, color: text, textBorderColor: background, textBorderWidth: 2 });
  const color = value => {
    if (typeof value !== 'string') return value;
    const index = (dark ? legacyColors : darkColors).indexOf(value.toLowerCase());
    return index < 0 ? value : (dark ? darkColors : legacyColors)[index];
  };
  if (option.backgroundColor && /^(#fff(fff)?|#f9f9f9|white|#1e1e2f|#202a3a)$/i.test(option.backgroundColor)) result.backgroundColor = background;
  const transform = (key, fn) => {
    if (option[key] != null) result[key] = mapComponents(option[key], fn);
  };
  transform('title', item => ({ ...item, textStyle: textStyle(item.textStyle), subtextStyle: textStyle(item.subtextStyle) }));
  transform('legend', item => ({ ...item, textStyle: textStyle(item.textStyle), inactiveColor: dark ? '#8993a3' : '#767676' }));
  transform('tooltip', item => ({ ...item, backgroundColor: background, borderColor: text, textStyle: textStyle(item.textStyle) }));
  transform('toolbox', item => ({
    ...item, iconStyle: { ...item.iconStyle, borderColor: text },
    feature: {
      ...item.feature,
      ...(item.feature?.dataView && { dataView: { ...item.feature.dataView, backgroundColor: background, textareaColor: background, textColor: text } }),
      ...(item.feature?.saveAsImage && { saveAsImage: { ...item.feature.saveAsImage, backgroundColor: background } })
    }
  }));
  for (const key of ['xAxis', 'yAxis', 'radiusAxis', 'angleAxis']) {
    transform(key, item => ({
      ...item, axisLabel: textStyle(item.axisLabel), nameTextStyle: textStyle(item.nameTextStyle),
      axisLine: { ...item.axisLine, lineStyle: { ...item.axisLine?.lineStyle, color: text, opacity: 0.5 } },
      splitLine: { ...item.splitLine, lineStyle: { ...item.splitLine?.lineStyle, color: text, opacity: 0.16 } }
    }));
  }
  transform('visualMap', item => ({ ...item, textStyle: textStyle(item.textStyle) }));
  transform('dataZoom', item => ({ ...item, textStyle: textStyle(item.textStyle) }));
  transform('calendar', item => ({
    ...item, dayLabel: textStyle(item.dayLabel), monthLabel: textStyle(item.monthLabel), yearLabel: textStyle(item.yearLabel)
  }));
  transform('geo', item => ({ ...item, label: label(item.label) }));
  transform('series', item => ({
    ...item,
    ...(item.itemStyle && { itemStyle: { ...item.itemStyle, color: color(item.itemStyle.color) } }),
    label: {
      ...label(item.label),
      ...(item.type === 'bar' && !item.label?.formatter && {
        formatter: params => Number(params.value) === 0 ? '' : params.value
      })
    },
    ...(item.type === 'bar' && typeof item.labelLayout !== 'function' && { labelLayout: { hideOverlap: true, ...item.labelLayout } }),
    ...(item.type === 'gauge' && { axisLabel: textStyle(item.axisLabel), title: textStyle(item.title), detail: textStyle(item.detail) }),
    ...(item.markLine && { markLine: { ...item.markLine, label: label(item.markLine.label) } }),
    ...(item.markPoint && { markPoint: { ...item.markPoint, label: label(item.markPoint.label) } })
  }));
  if (option.baseOption) result.baseOption = themeChartOption(option.baseOption, { dark, text, background });
  if (option.options) result.options = option.options.map(item => themeChartOption(item, { dark, text, background }));
  if (option.media) result.media = option.media.map(item => ({ ...item, option: themeChartOption(item.option, { dark, text, background }) }));
  return result;
}

export function withCoreuiTheme(echarts) {
  const initialized = new WeakSet();
  return {
    ...echarts,
    init(element, ...args) {
      const chart = echarts.init(element, ...args);
      if (initialized.has(chart)) return chart;
      initialized.add(chart);
      const setOption = chart.setOption.bind(chart);
      const dispose = chart.dispose.bind(chart);
      const palette = () => {
        const styles = getComputedStyle(element);
        return {
          dark: document.documentElement.dataset.coreuiTheme === 'dark',
          text: styles.color,
          background: styles.getPropertyValue('--cui-body-bg').trim() || '#ffffff'
        };
      };
      chart.setOption = (option, ...settings) => setOption(themeChartOption(option, palette()), ...settings);
      const observer = new MutationObserver(() => {
        if (!chart.isDisposed()) chart.setOption(chart.getOption());
      });
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-coreui-theme'] });
      // Sidebar transitions change the available space after window.resize has
      // fired. Follow the chart container through narrowing, dragging and tabs.
      let resizeFrame;
      const resizeObserver = new ResizeObserver(() => {
        cancelAnimationFrame(resizeFrame);
        resizeFrame = requestAnimationFrame(() => {
          if (!chart.isDisposed() && element.clientWidth > 0 && element.clientHeight > 0) chart.resize();
        });
      });
      // Respect charts initialized with an explicit, fixed drawing size.
      if (args[1]?.width == null && args[1]?.height == null) resizeObserver.observe(element);
      chart.dispose = () => {
        observer.disconnect();
        resizeObserver.disconnect();
        cancelAnimationFrame(resizeFrame);
        dispose();
      };
      return chart;
    }
  };
}
